const WpMovableRows = (() => {
    const containerSelector = '[data-wp-toggle~="movableRows"]';
    const rowSelector = '[data-movable-row]';
    const listSelector = '[data-movable-list]';
    const actionSelector = '[data-movable-action]';
    let updateScheduled = false;

    const getList = (container) => {
        return container.querySelector(listSelector) || container;
    };

    const getRows = (container) => {
        return Array.from(getList(container).querySelectorAll(rowSelector));
    };

    const isRowAvailable = (row, container) => {
        if (row.classList.contains('d-none')) {
            return false;
        }

        const checkboxSelector = getCheckboxSelector(container);
        if (!checkboxSelector) {
            return true;
        }

        const checkbox = row.querySelector(checkboxSelector);
        return !checkbox || !checkbox.disabled;
    };

    const getCheckboxSelector = (container) => {
        return container.dataset.movableCheckbox || '';
    };

    const getToggleSelector = (container) => {
        return container.dataset.movableToggle || '';
    };

    const getToggleCheckboxes = (container, row) => {
        const toggleSelector = getToggleSelector(container);
        if (!toggleSelector) {
            return [];
        }

        return Array.from(row.querySelectorAll(toggleSelector)).filter((checkbox) => {
            return checkbox.matches('input[type="checkbox"]') && !checkbox.hasAttribute('data-selectable-checkbox');
        });
    };

    const getCheckedRows = (container) => {
        const checkboxSelector = getCheckboxSelector(container);
        if (!checkboxSelector) {
            return [];
        }

        return getRows(container).filter((row) => {
            const checkbox = row.querySelector(checkboxSelector);
            return checkbox && !checkbox.disabled && checkbox.checked && isRowAvailable(row, container);
        });
    };

    const resetRowState = (container, row) => {
        const checkboxSelector = getCheckboxSelector(container);
        if (!checkboxSelector) {
            return;
        }

        const checkbox = row.querySelector(checkboxSelector);
        if (checkbox) {
            checkbox.checked = false;
            checkbox.dispatchEvent(new Event('change', { bubbles: true }));
        }
    };

    const toggleRowState = (container, row) => {
        const checkboxes = getToggleCheckboxes(container, row);
        if (!checkboxes.length) {
            return;
        }

        checkboxes.forEach((checkbox) => {
            checkbox.checked = !checkbox.checked;
            checkbox.dispatchEvent(new Event('change', { bubbles: true }));
        });
    };

    const resolveContainer = (button) => {
        const sourceSelector = button.dataset.movableSource;
        if (sourceSelector) {
            return document.querySelector(sourceSelector);
        }

        return button.closest(containerSelector);
    };

    const resolveTarget = (container) => {
        const targetSelector = container?.dataset.movableTarget;
        if (!targetSelector) return null;

        return document.querySelector(targetSelector);
    };

    const moveRows = (sourceContainer, rows) => {
        const targetContainer = resolveTarget(sourceContainer);
        if (!targetContainer || !rows.length) {
            return;
        }

        const targetList = getList(targetContainer);

        rows.forEach((row) => {
            resetRowState(sourceContainer, row);
            targetList.appendChild(row);
            toggleRowState(targetContainer, row);
        });

        scheduleUpdateButtons();
    };

    const updateButtons = () => {
        document.querySelectorAll(actionSelector).forEach((button) => {
            const sourceContainer = resolveContainer(button);
            if (!sourceContainer) {
                button.disabled = true;
                return;
            }

            const targetContainer = resolveTarget(sourceContainer);
            const rows = getRows(sourceContainer).filter((row) => isRowAvailable(row, sourceContainer));

            if (!targetContainer || !rows.length) {
                button.disabled = true;
                return;
            }

            if (button.dataset.movableAction === 'selected') {
                button.disabled = !getCheckboxSelector(sourceContainer) || !getCheckedRows(sourceContainer).length;
                return;
            }

            button.disabled = false;
        });
    };

    const scheduleUpdateButtons = () => {
        if (updateScheduled) {
            return;
        }

        updateScheduled = true;

        requestAnimationFrame(() => {
            updateScheduled = false;
            updateButtons();
        });
    };

    const sync = (context = document) => {
        updateButtons();
    };

    const handleContainerInteraction = (event) => {
        if (!event.target.closest(containerSelector) || event.target.closest(actionSelector)) {
            return;
        }

        scheduleUpdateButtons();
    };

    const handleClick = (event) => {
        const button = event.target.closest(actionSelector);
        if (!button) return;

        const sourceContainer = resolveContainer(button);
        if (!sourceContainer) return;

        const action = button.dataset.movableAction;
        let rows = [];

        if (action === 'selected') {
            rows = getCheckedRows(sourceContainer);
        } else if (action === 'all') {
            rows = getRows(sourceContainer).filter((row) => isRowAvailable(row, sourceContainer));
        }

        if (!rows.length) {
            scheduleUpdateButtons();
            return;
        }

        moveRows(sourceContainer, rows);
    };

    const init = () => {
        document.addEventListener('click', handleClick);
        document.addEventListener('click', handleContainerInteraction);
        document.addEventListener('change', scheduleUpdateButtons);
        sync();
    };

    return { init, updateButtons, sync };
})();

window.WpMovableRows = WpMovableRows;
WpMovableRows.init();
